import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";

import clubSingleClosedLeftM from "@/assets/UI/clubSingleClosedLeftM.webp";
import clubSingleClosedRightM from "@/assets/UI/clubSingleClosedRightM.webp";
import clubSingleClosedLeft from "@/assets/UI/clubSingleClosedLeft.webp";
import clubSingleClosedRight from "@/assets/UI/clubSingleClosedRight.webp";

import cls from "./ClubSingle.module.scss";

const floatSlow = {
  hidden: { opacity: 0.8 },
  show: {
    opacity: 1,
    scale: [1, 1.02, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.8 },
  show: {
    opacity: 1,
    scale: [1.01, 1, 1.01],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 24, mass: 0.9 },
  },
};

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
};

const KV = ({ k, v }) => (
  <div className={cls.kvRow}>
    <span className={cls.k}>{k}</span>
    <span className={cls.v}>{v || "-"}</span>
  </div>
);

/**
 * Prevents <img src=""> warnings.
 * If src is falsy, render nothing.
 * Works with motion.img too via `as`.
 */
const SafeImg = ({ as = "img", src, ...rest }) => {
  if (!src) return null;
  const Component = as;
  return <Component src={src} {...rest} />;
};

const ClubSingle = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { clubId } = useParams();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [memberState, setMemberState] = useState("");
  const [club, setClub] = useState(null);
  const [joinRequest, setJoinRequest] = useState(null);
  const [membership, setMembership] = useState(null);
  const [membershipMeta, setMembershipMeta] = useState(null);

  // Only for completelyNewMember: step 1 (closed) -> step 2 (real gate)
  const [newStep, setNewStep] = useState(1);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);
        setErr("");

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}`);
        if (!alive) return;

        setMemberState(data?.memberState || "");
        setClub(data?.club || null);
        setJoinRequest(data?.joinRequest || null);
        setMembership(data?.membership || null);
        setMembershipMeta(data?.membershipMeta || null);

        setNewStep(1);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Request failed");
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [clubId]);

  const isNew = memberState === "completelyNewMember";
  const isPending = memberState === "pendingToJoinMember";
  const isRejected = memberState === "rejectedToJoinMember";
  const isRemoved =
    memberState === "removedClubMember" ||
    memberState === "removedClubHost" ||
    memberState === "removedClubPartner";

  const isAcceptedOrActive =
    memberState === "acceptedToJoinMember" ||
    memberState === "activeClubMember" ||
    memberState === "activeClubHost" ||
    memberState === "activeClubPartner";

  const addressLine = useMemo(() => {
    if (!club) return "";
    const parts = [];
    if (club.clubNo) parts.push(club.clubNo);
    if (club.streetName) parts.push(club.streetName);
    if (club.cityName) parts.push(club.cityName);
    return parts.filter(Boolean).join(", ");
  }, [club]);

  // Always replace MobileOne/Two/DesktopOne/Two from API keys (signed urls)
  const mobileOneSrc = club?.mobileOne?.url || "";
  const mobileTwoSrc = club?.mobileTwo?.url || "";
  const desktopOneSrc = club?.desktopOne?.url || "";
  const desktopTwoSrc = club?.desktopTwo?.url || "";

  // Hero avatar MUST use clubAvatarKey (controller returns clubAvatar signed)
  const avatarUrl = club?.clubAvatar?.url || "";
  const clubName = club?.clubName || t("clubSingle.loadingTitle");

  const gate = useMemo(() => {
    let mLeft = club?.mobileHalfLeft?.url || "";
    let mRight = club?.mobileHalfRight?.url || "";
    let dLeft = club?.desktopHalfLeft?.url || "";
    let dRight = club?.desktopHalfRight?.url || "";

    let leftEnabled = true;
    let rightEnabled = true;

    let onLeft = () => navigate(`/lounge/clubs/single/${clubId}/about`);
    let onRight = () => navigate(`/lounge/clubs/single/${clubId}/home`);

    if (isNew && newStep === 1) {
      mLeft = clubSingleClosedLeftM;
      mRight = clubSingleClosedRightM;
      dLeft = clubSingleClosedLeft;
      dRight = clubSingleClosedRight;

      leftEnabled = false;
      rightEnabled = false;

      onLeft = () => {};
      onRight = () => {};
    }

    if (isNew && newStep === 2) {
      mLeft = club?.mobileHalfLeft?.url || "";
      mRight = club?.mobileHalfRight?.url || "";
      dLeft = club?.desktopHalfLeft?.url || "";
      dRight = club?.desktopHalfRight?.url || "";

      leftEnabled = true;
      rightEnabled = true;

      onLeft = () => navigate(`/lounge/clubs/single/${clubId}/about`);
      onRight = () => navigate(`/lounge/clubs/single/${clubId}/rules`);
    }

    if (isPending) {
      mLeft = club?.mobileHalfLeft?.url || "";
      dLeft = club?.desktopHalfLeft?.url || "";

      mRight = clubSingleClosedRightM;
      dRight = clubSingleClosedRight;

      leftEnabled = true;
      rightEnabled = false;

      onLeft = () => navigate(`/lounge/clubs/single/${clubId}/about`);
      onRight = () => {};
    }

    if (isRejected) {
      mLeft = club?.mobileHalfLeft?.url || "";
      dLeft = club?.desktopHalfLeft?.url || "";

      mRight = clubSingleClosedRightM;
      dRight = clubSingleClosedRight;

      leftEnabled = true;
      rightEnabled = false;

      onLeft = () => navigate(`/lounge/clubs/single/${clubId}/about`);
      onRight = () => {};
    }

    if (isRemoved) {
      mLeft = club?.mobileHalfLeft?.url || "";
      dLeft = club?.desktopHalfLeft?.url || "";

      mRight = clubSingleClosedRightM;
      dRight = clubSingleClosedRight;

      leftEnabled = true;
      rightEnabled = false;

      onLeft = () => navigate(`/lounge/clubs/single/${clubId}/about`);
      onRight = () => {};
    }

    if (isAcceptedOrActive) {
      mLeft = club?.mobileHalfLeft?.url || "";
      mRight = club?.mobileHalfRight?.url || "";
      dLeft = club?.desktopHalfLeft?.url || "";
      dRight = club?.desktopHalfRight?.url || "";

      leftEnabled = true;
      rightEnabled = true;

      onLeft = () => navigate(`/lounge/clubs/single/${clubId}/about`);
      onRight = () => navigate(`/lounge/clubs/single/${clubId}/home`);
    }

    return {
      mLeft,
      mRight,
      dLeft,
      dRight,
      leftEnabled,
      rightEnabled,
      onLeft,
      onRight,
    };
  }, [
    club,
    clubId,
    isNew,
    isPending,
    isRejected,
    isRemoved,
    isAcceptedOrActive,
    navigate,
    newStep,
  ]);

  const handleLeftHalf = () => gate.onLeft();
  const handleRightHalf = () => gate.onRight();

  const scrollToTop = () => window.scrollTo({ top: 200, behavior: "smooth" });

  const onNo = () => {
    scrollToTop();
    navigate(-1);
  };

  const onYes = () => {
    scrollToTop();
    setNewStep(2);
  };

  // ✅ IMPORTANT: gating AFTER hooks (prevents "Rendered more hooks" error)
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  // Optional: keep consistent and gate error after hooks too
  if (err) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <div className={cls.heroOverlayErr}>{err}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 0-.59l7.4-2.47a.5.5 0 01.63.63z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch} ${cls.clubMiniBtnActive}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 5L20.49 19l-5-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* ===== Club Single Hero ===== */}
            <section className={cls.searchHero}>
              <div className={cls.heroStage}>
                {/* Mobile */}
                <div className={cls.mobileOnly} aria-hidden="true">
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z0}`}
                    src={mobileOneSrc}
                    alt=""
                    variants={floatSlow}
                    initial="hidden"
                    animate="show"
                  />
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z1}`}
                    src={mobileTwoSrc}
                    alt=""
                    variants={floatSlowAlt}
                    initial="hidden"
                    animate="show"
                  />

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.leftHalf}`}
                    onClick={handleLeftHalf}
                    aria-label="Left gate"
                    disabled={!gate.leftEnabled}
                    data-disabled={!gate.leftEnabled}
                  >
                    {gate.mLeft ? (
                      <img
                        src={gate.mLeft}
                        alt=""
                        className={`${cls.heroHalfImg} ${cls.z3}`}
                        draggable="false"
                      />
                    ) : null}
                  </button>

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.rightHalf}`}
                    onClick={handleRightHalf}
                    aria-label="Right gate"
                    disabled={!gate.rightEnabled}
                    data-disabled={!gate.rightEnabled}
                  >
                    {gate.mRight ? (
                      <img
                        src={gate.mRight}
                        alt=""
                        className={`${cls.heroHalfImg} ${cls.z3}`}
                        draggable="false"
                      />
                    ) : null}
                  </button>
                </div>

                {/* Desktop */}
                <div className={cls.desktopOnly} aria-hidden="true">
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z0}`}
                    src={desktopOneSrc}
                    alt=""
                    variants={floatSlow}
                    initial="hidden"
                    animate="show"
                  />
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z1}`}
                    src={desktopTwoSrc}
                    alt=""
                    variants={floatSlowAlt}
                    initial="hidden"
                    animate="show"
                  />

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.leftHalf}`}
                    onClick={handleLeftHalf}
                    aria-label="Left gate"
                    disabled={!gate.leftEnabled}
                    data-disabled={!gate.leftEnabled}
                  >
                    {gate.dLeft ? (
                      <img
                        src={gate.dLeft}
                        alt=""
                        className={`${cls.heroHalfImg} ${cls.z3}`}
                        draggable="false"
                      />
                    ) : null}
                  </button>

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.rightHalf}`}
                    onClick={handleRightHalf}
                    aria-label="Right gate"
                    disabled={!gate.rightEnabled}
                    data-disabled={!gate.rightEnabled}
                  >
                    {gate.dRight ? (
                      <img
                        src={gate.dRight}
                        alt=""
                        className={`${cls.heroHalfImg} ${cls.z3}`}
                        draggable="false"
                      />
                    ) : null}
                  </button>
                </div>
              </div>

              <motion.div
                className={cls.heroCardPushed}
                variants={slideInUp}
                initial="hidden"
                animate="show"
              >
                <div className={cls.heroTopRow}>
                  <div className={cls.avatarWrap}>
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={clubName}
                        className={cls.avatar}
                        draggable="false"
                      />
                    ) : (
                      <div className={cls.avatarFallback} />
                    )}
                  </div>

                  <div className={cls.heroTopMeta}>
                    <span className={cls.searchPromptLabel}>
                      {t("clubSingle.hero.label")}
                    </span>
                    <h2 className={cls.searchPromptTitle}>{clubName}</h2>
                    <p className={cls.heroAddress}>{addressLine}</p>
                  </div>
                </div>

                <p className={cls.searchPromptTextSecondary}>
                  {club?.clubDescription || t("clubSingle.noDesc")}
                </p>

                {/* completelyNewMember */}
                {isNew ? (
                  <>
                    {newStep === 1 ? (
                      <div className={cls.stateBox} data-state={memberState}>
                        <div className={cls.stateTitle}>
                          {t("clubSingle.state.new.title")}
                        </div>
                        <p className={cls.stateText}>
                          {t("clubSingle.state.new.q")}
                        </p>

                        <div className={cls.heroButtonsRow}>
                          <button
                            type="button"
                            className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
                            onClick={onNo}
                          >
                            {t("common.no")}
                          </button>
                          <button
                            type="button"
                            className={`${cls.nuBtn} ${cls.nuBtnPrimary}`}
                            onClick={onYes}
                          >
                            {t("common.yes")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={cls.stateBox} data-state={memberState}>
                        <div className={cls.stateTitle}>
                          {t("clubSingle.state.new.step2Title")}
                        </div>
                        <p className={cls.stateText}>
                          {t("clubSingle.state.new.step2TextRules")}
                        </p>
                      </div>
                    )}
                  </>
                ) : null}

                {/* pendingToJoinMember */}
                {isPending ? (
                  <div className={cls.stateBox} data-state={memberState}>
                    <div className={cls.stateTitle}>
                      {t("clubSingle.state.pending.title")}
                    </div>
                    <p className={cls.stateText}>
                      {t("clubSingle.state.pending.text")}
                    </p>

                    <div className={cls.detailCard}>
                      <div className={cls.detailTitle}>
                        {t("clubSingle.joinRequest.title")}
                      </div>
                      <div className={cls.kvGrid}>
                        <KV
                          k={t("clubSingle.joinRequest.status")}
                          v={joinRequest?.status}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.createdAt")}
                          v={fmtDate(joinRequest?.createdAt)}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.updatedAt")}
                          v={fmtDate(joinRequest?.updatedAt)}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.message")}
                          v={joinRequest?.message || joinRequest?.note}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.requestedRole")}
                          v={joinRequest?.requestedRole}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* rejectedToJoinMember */}
                {isRejected ? (
                  <div className={cls.stateBox} data-state={memberState}>
                    <div className={cls.stateTitle}>
                      {t("clubSingle.state.rejected.title")}
                    </div>
                    <p className={cls.stateText}>
                      {t("clubSingle.state.rejected.text")}
                    </p>

                    <div className={cls.detailCard}>
                      <div className={cls.detailTitle}>
                        {t("clubSingle.joinRequest.title")}
                      </div>
                      <div className={cls.kvGrid}>
                        <KV
                          k={t("clubSingle.joinRequest.status")}
                          v={joinRequest?.status}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.createdAt")}
                          v={fmtDate(joinRequest?.createdAt)}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.updatedAt")}
                          v={fmtDate(joinRequest?.updatedAt)}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.message")}
                          v={joinRequest?.message || joinRequest?.note}
                        />
                        <KV
                          k={t("clubSingle.joinRequest.requestedRole")}
                          v={joinRequest?.requestedRole}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* removed */}
                {isRemoved ? (
                  <div className={cls.stateBox} data-state={memberState}>
                    <div className={cls.stateTitle}>
                      {t("clubSingle.state.removed.title")}
                    </div>
                    <p className={cls.stateText}>
                      {t("clubSingle.state.removed.text")}
                    </p>

                    <div className={cls.detailCard}>
                      <div className={cls.detailTitle}>
                        {t("clubSingle.membership.title")}
                      </div>
                      <div className={cls.kvGrid}>
                        <KV
                          k={t("clubSingle.membership.role")}
                          v={membership?.role || membershipMeta?.role}
                        />
                        <KV
                          k={t("clubSingle.membership.status")}
                          v={membership?.status || membershipMeta?.status}
                        />
                        <KV
                          k={t("clubSingle.membership.assignedBy")}
                          v={membership?.assignedBy}
                        />
                        <KV
                          k={t("clubSingle.membership.createdAt")}
                          v={fmtDate(membership?.createdAt)}
                        />
                        <KV
                          k={t("clubSingle.membership.updatedAt")}
                          v={fmtDate(membership?.updatedAt)}
                        />
                        <KV
                          k={t("clubSingle.membership.note")}
                          v={membership?.note || membership?.reason}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* accepted/active */}
                {isAcceptedOrActive ? (
                  <div className={cls.stateBox} data-state={memberState}>
                    <div className={cls.stateTitle}>
                      {t("clubSingle.state.active.title")}
                    </div>
                    <p className={cls.stateText}>
                      {t("clubSingle.state.active.text")}
                    </p>

                    <div className={cls.stateMiniLine}>
                      <span className={cls.pill}>
                        {t("clubSingle.state.active.role")}:{" "}
                        {membershipMeta?.role || membership?.role || "-"}
                      </span>
                      <span className={cls.pill}>
                        {t("clubSingle.state.active.status")}:{" "}
                        {membershipMeta?.status || membership?.status || "-"}
                      </span>
                    </div>
                  </div>
                ) : null}
              </motion.div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubSingle;
